import React, { memo, useRef, useState } from 'react';

import {
  CheckIcon,
  ClockIcon,
  CopyIcon,
  GlobeIcon,
  HouseIcon,
  MailIcon,
  MapPinIcon,
  PhoneIcon,
  UserIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { CollapsibleSection } from '@/components/atoms/CollapsibleSection/CollapsibleSection';
import { EditableText } from '@/components/atoms/EditableText/EditableText';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useConversationContext } from '@/hooks/useConversationContext';
import { cn } from '@/lib/utils';
import { useInformationSection } from '@/modules/humanChat/hooks/useInformationSection';
import { MenuItem } from '@/modules/humanChat/models';
import {
  visitorAddressSchema,
  visitorDisplayNameSchema,
  visitorEmailSchema,
  visitorPhoneSchema,
} from '@/modules/humanChat/validator';
import { getPermissions } from '@/redux/permissions/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

const ICON_STYLES = { size: 16, color: 'var(--muted-foreground)' };

const COLLAPSED_COUNT = 5;

export const IconByField: Record<string, React.JSX.Element> = {
  display_name: <UserIcon {...ICON_STYLES} />,
  email: <MailIcon {...ICON_STYLES} />,
  local_time: <ClockIcon {...ICON_STYLES} />,
  languages: <GlobeIcon {...ICON_STYLES} />,
  address: <HouseIcon {...ICON_STYLES} />,
  phone: <PhoneIcon {...ICON_STYLES} />,
  location: <MapPinIcon {...ICON_STYLES} />,
};

const InputSkeleton = () => <Skeleton className="h-5 w-20" />;

const CopyValueButton = ({ value, label }: { value: string; label: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Button
      type="button"
      variant="ghost-neutral"
      size="icon-sm"
      aria-label={label}
      onClick={handleCopy}
      className="ml-1 size-6 opacity-0 transition-opacity group-focus-within/row:hidden group-hover/row:opacity-100 focus-visible:opacity-100"
    >
      {copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
    </Button>
  );
};

type EditableFieldProps = {
  canEdit: boolean;
  canClear?: boolean;
  validationSchema?: unknown;
  placeholder?: string;
};

type ListItemProps = {
  renderIcon: React.ReactNode;
  isLoading: boolean;
  item: MenuItem;
  isEditable: boolean;
  showCopy?: boolean;
  copyLabel?: string;
  editableFieldsProps: EditableFieldProps;
  onSubmit: (key: string, value: string, prevValue: string) => void;
};

const ListItem = ({
  renderIcon,
  isLoading,
  item,
  isEditable,
  showCopy,
  copyLabel,
  editableFieldsProps,
  onSubmit,
}: ListItemProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const textContent = isEditable ? (
    <EditableText
      color="var(--foreground)"
      containerRef={containerRef}
      defaultValue={item.text}
      onSubmit={(value) => onSubmit(item.id ?? '', value, item.text)}
      {...editableFieldsProps}
    />
  ) : (
    <span className="ml-1">{item.text}</span>
  );

  const trimmedText = item.text?.trim();

  return (
    <li
      className={cn(
        'group/row flex h-8 items-center gap-2 text-sm leading-5 font-normal',
        // Draw the inline editor's focus ring inside the input so it doesn't spill
        // over adjacent rows in this dense list. Scoped here; shared atom untouched.
        '[&_input:focus-visible]:ring-offset-0 [&_input:focus-visible]:ring-inset',
        !isLoading && 'gap-0.5'
      )}
    >
      <div className="mr-1 shrink-0">{renderIcon}</div>
      <div className="flex min-w-0 focus-within:flex-1" ref={containerRef}>
        {isLoading ? <InputSkeleton /> : textContent}
      </div>
      {!isLoading && showCopy && trimmedText && (
        <CopyValueButton value={trimmedText} label={copyLabel ?? trimmedText} />
      )}
    </li>
  );
};

export const InformationSection = memo(() => {
  // Local state
  const [expanded, setExpanded] = useState(false);

  // Hooks
  const { t } = useTranslation();
  const { isLoading, updateUser } = useConversationContext();
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'conversations', actions.WRITE)
  );
  const { visibleItems } = useInformationSection();

  // Handlers
  const handleSubmit = (key: string, value: string, prevValue: string) => {
    const trimmedValue = value?.trim();
    if (!trimmedValue && !prevValue) {
      return;
    }
    if (prevValue === value) {
      return;
    }

    updateUser({ [key]: trimmedValue || null });
  };

  const editableFields: Record<
    string,
    { icon: React.JSX.Element; validationSchema?: unknown; placeholder: string }
  > = {
    display_name: {
      icon: (
        <InfoTooltip content={t('field.display_name')} showArrow>
          {IconByField.display_name}
        </InfoTooltip>
      ),
      validationSchema: visitorDisplayNameSchema,
      placeholder: t('contacts.set_display_name'),
    },
    email: {
      icon: (
        <InfoTooltip content={t('field.email')} showArrow>
          {IconByField.email}
        </InfoTooltip>
      ),
      validationSchema: visitorEmailSchema,
      placeholder: t('contacts.set_email'),
    },
    address: {
      icon: (
        <InfoTooltip content={t('field.address')} showArrow>
          {IconByField.address}
        </InfoTooltip>
      ),
      validationSchema: visitorAddressSchema,
      placeholder: t('contacts.set_address'),
    },
    phone: {
      icon: (
        <InfoTooltip content={t('field.phone')} showArrow>
          {IconByField.phone}
        </InfoTooltip>
      ),
      validationSchema: visitorPhoneSchema,
      placeholder: t('contacts.set_phone'),
    },
  };

  const showToggle = visibleItems.length > COLLAPSED_COUNT;
  const displayedItems = expanded ? visibleItems : visibleItems.slice(0, COLLAPSED_COUNT);

  return (
    <section>
      <CollapsibleSection
        title={t('common.information')}
        showBorder={false}
        storageKey="humanChat.sidebar.information"
      >
        <ul className="space-y-1 px-1.5 pb-3">
          {displayedItems.map((item) => {
            const fieldConfig = editableFields[item.id];
            return (
              <ListItem
                key={item.id}
                isLoading={isLoading}
                renderIcon={(fieldConfig?.icon ?? item.icon) as React.ReactNode}
                item={item}
                isEditable={Boolean(fieldConfig?.validationSchema && fieldConfig?.placeholder)}
                showCopy={Boolean(fieldConfig)}
                copyLabel={t('common.copy')}
                editableFieldsProps={{
                  canEdit: canWrite,
                  // Contact fields are optional — clearing deletes the value.
                  canClear: true,
                  validationSchema: fieldConfig?.validationSchema,
                  placeholder: fieldConfig?.placeholder,
                }}
                onSubmit={handleSubmit}
              />
            );
          })}

          {showToggle && (
            <li className="flex h-8 items-center gap-1 text-sm leading-5 font-normal">
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  setExpanded((prev) => !prev);
                }}
                variant="link-secondary"
                size="sm"
                className="h-auto px-0 py-0.5 text-sm text-primary [&>span]:px-0"
              >
                {expanded ? t('common.show_less') : t('common.show_all')}
              </Button>
            </li>
          )}
        </ul>
      </CollapsibleSection>
    </section>
  );
});

InformationSection.displayName = 'InformationSection';
